import { useEffect, useState } from "react";
import { ArrowLeft, ChevronRight, FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { type Classroom, type ClassroomReportRow } from "./types";
import { ReportDateSearch } from "./ReportDateSearch";

export function ReportList({
  classroomId,
  onBack,
  onSelectReport,
}: {
  classroomId: number;
  onBack: () => void;
  onSelectReport: (r: ClassroomReportRow) => void;
}) {
  const [reports, setReports] = useState<ClassroomReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [classroomName, setClassroomName] = useState<string | null>(null);
  const [teacherName, setTeacherName] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(false);

    const ctrl = new AbortController();

    const load = async () => {
      try {
        const nameRes = await fetch(
          `/api/internal/admin/classrooms/${classroomId}`,
          { signal: ctrl.signal },
        );
        if (nameRes.ok) {
          const data: Classroom = await nameRes.json();
          setClassroomName(data.name);
          setTeacherName(data.teacher_name ?? null);
        }

        const reportsRes = await fetch(
          `/api/internal/admin/classrooms/${classroomId}/reports`,
          { signal: ctrl.signal },
        );
        if (!reportsRes.ok) throw new Error(`${reportsRes.status}`);
        const data: ClassroomReportRow[] = await reportsRes.json();
        setReports(data);
      } catch (e) {
        if ((e as { name?: string }).name !== "AbortError") setError(true);
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => ctrl.abort();
  }, [classroomId, retryKey]);

  const handleClick = async (report: ClassroomReportRow) => {
    const params = new URLSearchParams({
      classroomId: String(report.classroom_id),
      date: report.report_date,
    });
    const r = await fetch(
      `/api/internal/admin/classroom-reports/lookup?${params}`,
    );
    if (!r.ok) return;
    const data: ClassroomReportRow = await r.json();
    onSelectReport(data);
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0"
          onClick={onBack}
        >
          <ArrowLeft className="size-4" />
        </Button>
        <h2 className="text-sm font-semibold text-foreground">
          {classroomName ?? `Classroom ${classroomId}`} — Reports
        </h2>
        <span className="text-xs text-muted-foreground">ID: {classroomId}</span>
        {teacherName && (
          <span className="text-xs text-muted-foreground">
            · Teacher: {teacherName}
          </span>
        )}
      </div>

      <div className="rounded-xl border bg-card shadow-sm px-5 py-4 flex flex-col gap-3">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Search by date
        </p>
        <ReportDateSearch
          fixedClassroomId={classroomId}
          onResult={onSelectReport}
        />
      </div>

      {loading ? (
        <div className="flex flex-col gap-2">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="rounded-xl border bg-card px-5 py-4 flex items-center gap-4"
            >
              <Skeleton className="size-9 rounded-lg" />
              <div className="flex-1 flex flex-col gap-1.5">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="flex items-center gap-3 px-1">
          <p className="text-sm text-destructive">Failed to load reports.</p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setRetryKey((k) => k + 1)}
          >
            Retry
          </Button>
        </div>
      ) : reports.filter(
          (r) => r.status === "generated" || r.status === "stale",
        ).length === 0 ? (
        <p className="text-sm text-muted-foreground px-1">No reports yet.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {reports
            .filter((r) => r.status === "generated" || r.status === "stale")
            .map((report) => (
              <button
                key={report.id}
                onClick={() => handleClick(report)}
                className="w-full text-left rounded-xl border bg-card shadow-sm px-5 py-4 flex items-center gap-4 hover:bg-accent/50 transition-colors group"
              >
                <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                  <FileText className="size-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-foreground">
                    {report.report_date}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {report.timezone}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Badge
                    variant={
                      report.status === "generated" ? "default" : "secondary"
                    }
                    className="text-xs capitalize"
                  >
                    {report.status}
                  </Badge>
                  <ChevronRight className="size-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                </div>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
